import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HelpdeskRulesAPI } from "../../../../api/helpdeskRules";
import ActionListEditor from "../ActionListEditor";

const EMPTY_RULE = {
  name: "",
  description: "",
  scope_type: "global",
  scope_id: null,
  event_type: "ticket.created",
  conditions: {
    all: [],
    any: [],
  },
  actions: [
    {
      action_type: "notify",
      action_order: 0,
      params: {},
    },
  ],
};

export default function RuleEditor({ mode, ruleId }) {
  const navigate = useNavigate();

  const [rule, setRule] = useState(EMPTY_RULE);
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [fields, setFields] = useState([]);
  const [operators, setOperators] = useState([]);

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [users, setUsers] = useState([]);

  const [testTicketId, setTestTicketId] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testRunning, setTestRunning] = useState(false);

  useEffect(() => {
    loadEditorOptions();
  }, []);

  useEffect(() => {
    if (mode === "edit") {
      loadRule();
    }
  }, [mode, ruleId]);

  async function loadEditorOptions() {
    const load = async (request, setter, key) => {
      try {
        if (typeof request !== "function") {
          console.error(`Rule editor API method missing for: ${key}`);
          setter([]);
          return;
        }

        const data = await request();
        setter(data?.[key] || []);
      } catch (err) {
        console.error(`Failed to load rule editor option: ${key}`, err);
        setter([]);
      }
    };

    await Promise.all([
      load(HelpdeskRulesAPI.listLifecycleOptions, setTypeOptions, "types"),
      load(
        HelpdeskRulesAPI.listNotifications,
        setNotifications,
        "notifications"
      ),
      load(
        HelpdeskRulesAPI.listEvents,
        setTriggers,
        "events"
      ),
      load(
        HelpdeskRulesAPI.listFields,
        setFields,
        "fields"
      ),
      load(
        HelpdeskRulesAPI.listOperators,
        setOperators,
        "operators"
      ),
      load(
        HelpdeskRulesAPI.listCategoryOptions,
        setCategoryOptions,
        "options"
      ),
      load(
        HelpdeskRulesAPI.listDepartmentOptions,
        setDepartmentOptions,
        "options"
      ),
      load(
        HelpdeskRulesAPI.listPriorityOptions,
        setPriorityOptions,
        "options"
      ),
      load(
        HelpdeskRulesAPI.listStatusOptions,
        setStatusOptions,
        "options"
      ),
      load(
        HelpdeskRulesAPI.listSchoolOptions,
        setSchoolOptions,
        "options"
      ),
      load(
        HelpdeskRulesAPI.listSubcategoryOptions,
        setSubcategoryOptions,
        "options"
      ),
      load(
        HelpdeskRulesAPI.listUsers,
        setUsers,
        "users"
      ),
    ]);
  }

  async function loadRule() {
    try {
      const data = await HelpdeskRulesAPI.get(ruleId);

      let conditions = data.conditions;

      if (typeof conditions === "string") {
        try {
          conditions = JSON.parse(conditions || "{}");
        } catch {
          conditions = {};
        }
      }

      setRule({
        ...data,
        conditions: {
          all: conditions?.all || [],
          any: conditions?.any || [],
        },
        actions:
          data.actions && data.actions.length > 0
            ? data.actions
            : EMPTY_RULE.actions,
      });
    } finally {
      setLoading(false);
    }
  }

  function updateCondition(group, index, changes) {
    const updated = [...(rule.conditions?.[group] || [])];

    updated[index] = {
      ...updated[index],
      ...changes,
    };

    setRule({
      ...rule,
      conditions: {
        ...rule.conditions,
        [group]: updated,
      },
    });
  }

  function addCondition(group) {
    setRule({
      ...rule,
      conditions: {
        ...rule.conditions,
        [group]: [
          ...(rule.conditions?.[group] || []),
          {
            field: "",
            operator: "",
            value: "",
          },
        ],
      },
    });
  }

  function removeCondition(group, index) {
    const updated = [...(rule.conditions?.[group] || [])];
    updated.splice(index, 1);

    setRule({
      ...rule,
      conditions: {
        ...rule.conditions,
        [group]: updated,
      },
    });
  }

  function getValueOptions(field) {
    switch (field) {
      case "ticket.category_id":
        return categoryOptions;

      case "ticket.department_id":
        return departmentOptions;

      case "ticket.priority_id":
        return priorityOptions;

      case "ticket.ticket_type_id":
        return typeOptions;

      case "ticket.status_id":
        return statusOptions;

      case "ticket.school_id":
        return schoolOptions;

      case "ticket.subcategory_id":
        return subcategoryOptions;

      default:
        return null;
    }
  }

  function renderCondition(group, condition, index) {
    const selectedField = fields.find(
      (field) => field.key === condition.field
    );

    const fieldType = selectedField?.type || "text";
    const valueOptions = getValueOptions(condition.field);

    return (
      <div
        key={index}
        className="grid grid-cols-12 gap-2 items-start border rounded p-3"
      >
        <div className="col-span-4">
          <select
            className="border rounded p-2 w-full"
            value={condition.field || ""}
            onChange={(e) =>
              updateCondition(group, index, {
                field: e.target.value,
                value: "",
              })
            }
          >
            <option value="">Select field</option>

            {fields.map((field) => (
              <option key={field.key} value={field.key}>
                {field.label}
              </option>
            ))}
          </select>
        </div>

        <div className="col-span-3">
          <select
            className="border rounded p-2 w-full"
            value={condition.operator || ""}
            onChange={(e) =>
              updateCondition(group, index, {
                operator: e.target.value,
              })
            }
          >
            <option value="">Select operator</option>

            {operators.map((operator) => (
              <option key={operator.key} value={operator.key}>
                {operator.label}
              </option>
            ))}
          </select>
        </div>

        <div className="col-span-4">
          {valueOptions ? (
            <select
              className="border rounded p-2 w-full"
              value={condition.value ?? ""}
              onChange={(e) =>
                updateCondition(group, index, {
                  value: e.target.value,
                })
              }
            >
              <option value="">Select value</option>

              {condition.value && !valueOptions.some(option => String(option.id) === String(condition.value)) && (
                <option value={condition.value}>Saved value #{condition.value}</option>
              )}
              {valueOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          ) : fieldType === "boolean" ? (
            <select
              className="border rounded p-2 w-full"
              value={condition.value ?? ""}
              onChange={(e) =>
                updateCondition(group, index, {
                  value: e.target.value,
                })
              }
            >
              <option value="">Select value</option>
              <option value="true">True</option>
              <option value="false">False</option>
            </select>
          ) : (
            <input
              type={fieldType === "number" ? "number" : "text"}
              className="border rounded p-2 w-full"
              value={condition.value ?? ""}
              onChange={(e) =>
                updateCondition(group, index, {
                  value: e.target.value,
                })
              }
              placeholder="Value"
            />
          )}
        </div>

        <div className="col-span-1 text-right">
          <button
            type="button"
            onClick={() => removeCondition(group, index)}
            className="text-red-600 px-2 py-2"
            title="Remove condition"
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  async function save() {
    try {
      setSaving(true);

      const payload = {
        ...rule,
        conditions: {
          all: rule.conditions?.all || [],
          any: rule.conditions?.any || [],
        },
      };

      if (mode === "create") {
        await HelpdeskRulesAPI.create(payload);
      } else {
        await HelpdeskRulesAPI.update(ruleId, payload);
      }

      navigate("/helpdesk/automation/rules");
    } finally {
      setSaving(false);
    }
  }

  async function runRuleTest() {
    if (!testTicketId) {
      return;
    }

    try {
      setTestRunning(true);

      const data = await HelpdeskRulesAPI.testRule({
        rule,
        ticketId: Number(testTicketId),
      });

      setTestResult(data);
    } catch (err) {
      console.error("Rule test failed", err);
    } finally {
      setTestRunning(false);
    }
  }

  if (loading) {
    return <div>Loading rule…</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">
          {mode === "create" ? "New rule" : "Edit rule"}
        </h2>

        <p className="text-sm text-gray-600 mt-1">
          Run actions automatically when an event meets the conditions below.
        </p>
      </div>

      <div className="border rounded p-5 space-y-4">
        <h3 className="font-semibold">Rule details</h3>

        <div>
          <label className="block text-sm font-medium mb-1">
            Name
          </label>

          <input
            className="w-full border rounded p-2"
            value={rule.name || ""}
            onChange={(e) =>
              setRule({
                ...rule,
                name: e.target.value,
              })
            }
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Description
          </label>

          <textarea
            className="w-full border rounded p-2"
            value={rule.description || ""}
            onChange={(e) =>
              setRule({
                ...rule,
                description: e.target.value,
              })
            }
          />
        </div>
      </div>

      <div className="border rounded p-5 space-y-4">
        <h3 className="font-semibold">When this happens</h3>

        <div>
          <label className="block text-sm font-medium mb-1">
            Trigger
          </label>

          <select
            className="border rounded p-2 w-full"
            value={rule.event_type || ""}
            onChange={(e) =>
              setRule({
                ...rule,
                event_type: e.target.value,
              })
            }
          >
            {triggers.map((trigger) => (
              <option key={trigger.id} value={trigger.id}>
                {trigger.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Scope
          </label>

          <select
            className="border rounded p-2 w-full"
            value={rule.scope_type}
            onChange={(e) =>
              setRule({
                ...rule,
                scope_type: e.target.value,
                scope_id:
                  e.target.value === "global"
                    ? null
                    : rule.scope_id,
              })
            }
          >
            <option value="global">Global (all schools)</option>
            <option value="school">Single school</option>
          </select>
        </div>

        {rule.scope_type === "school" && (
          <div>
            <label className="block text-sm font-medium mb-1">
              School
            </label>

            <select
              className="border rounded p-2 w-full"
              value={rule.scope_id || ""}
              onChange={(e) =>
                setRule({
                  ...rule,
                  scope_id: Number(e.target.value),
                })
              }
            >
              <option value="">Select school</option>

              {schoolOptions.map((school) => (
                <option key={school.id} value={school.id}>
                  {school.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="border rounded p-5 space-y-5">
        <div>
          <h3 className="font-semibold">Conditions</h3>
          <p className="text-sm text-gray-600 mt-1">
            ALL conditions must match. If ANY conditions are supplied, at
            least one of those must also match.
          </p>
        </div>

        <div className="space-y-3">
          <div className="font-medium text-sm">
            All of these conditions
          </div>

          {(rule.conditions?.all || []).map((condition, index) =>
            renderCondition("all", condition, index)
          )}

          <button
            type="button"
            onClick={() => addCondition("all")}
            className="px-3 py-2 border rounded text-sm"
          >
            + Add ALL condition
          </button>
        </div>

        <div className="space-y-3 pt-4 border-t">
          <div className="font-medium text-sm">
            Any of these conditions
          </div>

          {(rule.conditions?.any || []).map((condition, index) =>
            renderCondition("any", condition, index)
          )}

          <button
            type="button"
            onClick={() => addCondition("any")}
            className="px-3 py-2 border rounded text-sm"
          >
            + Add ANY condition
          </button>
        </div>
      </div>

      <div className="border rounded p-5 space-y-4">
        <div>
          <h3 className="font-semibold">Actions</h3>
          <p className="text-sm text-gray-600 mt-1">
            Actions run in order from top to bottom.
          </p>
        </div>

        <ActionListEditor
          actions={rule.actions || []}
          onChange={(actions) =>
            setRule({
              ...rule,
              actions,
            })
          }
          notifications={notifications}
          statusOptions={statusOptions}
          categoryOptions={categoryOptions}
          subcategoryOptions={subcategoryOptions}
          users={users}
        />
      </div>

      <div className="border rounded p-5 space-y-3">
        <h3 className="font-semibold">Test rule</h3>

        <div className="flex gap-2">
          <input
            type="number"
            placeholder="Ticket ID"
            value={testTicketId}
            onChange={(e) => setTestTicketId(e.target.value)}
            className="border rounded px-2 py-2 text-sm w-40"
          />

          <button
            type="button"
            onClick={runRuleTest}
            disabled={testRunning}
            className="bg-blue-600 text-white px-3 py-2 rounded text-sm disabled:opacity-50"
          >
            {testRunning ? "Testing..." : "Run test"}
          </button>
        </div>

        {testResult && (
          <div className="text-sm bg-gray-50 border p-3 rounded">
            <div className="font-semibold mb-2">
              Result:{" "}
              {testResult.matched
                ? "Rule matched ✔"
                : "Rule did not match ✖"}
            </div>

            {Array.isArray(testResult.conditions) &&
              testResult.conditions.map((condition, index) => (
                <div
                  key={index}
                  className="border-t first:border-t-0 py-2"
                >
                  <div>
                    <strong>{condition.field}</strong>
                  </div>

                  <div className="text-xs text-gray-600">
                    Expected: {String(condition.expected)}
                  </div>

                  <div className="text-xs text-gray-600">
                    Actual: {String(condition.actual)}
                  </div>

                  <div>
                    {condition.result ? "✔ matched" : "✖ failed"}
                  </div>
                </div>
              ))}

            {Array.isArray(testResult.actions) &&
              testResult.actions.length > 0 && (
                <div className="border-t pt-2 mt-2">
                  <div className="font-semibold">Actions</div>

                  {testResult.actions.map((action, index) => (
                    <div key={index}>• {action}</div>
                  ))}
                </div>
              )}
          </div>
        )}
      </div>

      <div className="border rounded p-5">
        <details>
          <summary className="cursor-pointer font-medium">
            Rule preview
          </summary>

          <pre className="bg-gray-100 p-4 text-xs overflow-auto rounded border mt-3">
            {JSON.stringify(rule, null, 2)}
          </pre>
        </details>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save rule"}
        </button>

        <button
          type="button"
          onClick={() => navigate("/helpdesk/automation/rules")}
          disabled={saving}
          className="px-4 py-2 border rounded"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}